import { isEmpty, isEqual } from 'lodash'
import { isRootParentSectionId } from '../lib/constants'
import {
  getSectionSortOrderForDoc,
  getSectionTitleDisplayNoForDoc,
  sortDocIds,
} from '../lib/libsV3'
import { compactPublishedDocsForActivityLog } from './publishedDocsDelta'

export {
  compactPublishedDocsForActivityLog,
  expandPublishedDocsFromActivityLog,
} from './publishedDocsDelta'

const CHANGE_TITLE_PREFIX = /^\[(?:更新|新增|删除|刪除|Update|Add|Delete)\]\s*/

/** 區塊 exampleName 查找：略過空 map、以及 field.examples 的字串值 */
export const mergeExampleMaps = (...maps) => {
  const out = {}
  maps.forEach((m) => {
    if (!m || typeof m !== 'object' || Array.isArray(m)) return
    Object.entries(m).forEach(([id, val]) => {
      if (out[id]) return
      if (!val || typeof val !== 'object' || Array.isArray(val)) return
      if (val.exampleName == null || val.exampleName === '') return
      out[id] = val
    })
  })
  return out
}

/**
 * 與 DraftChange 子本設定變更列對齊的可比對欄位
 */
export const UPDATED_DOC_COMPARE_FIELDS = [
  { key: 'docTitle', label: 'Title' },
  { key: 'subName', label: 'Sub Name' },
  { key: 'currency', label: 'Currency' },
  { key: 'platforms', label: 'Platforms', isPlatforms: true },
  { key: 'excludedPlatforms', label: 'Excluded Platforms', isPlatforms: true },
]

/**
 * 產出「子本設定變更」列（與 DraftChange.selectedUpdatedDocFieldDiffs 同形）
 * @param {object} [platformsMap] id → 顯示名稱（寫入 beforeLabels/afterLabels 供搜尋）
 */
export const buildUpdatedDocFieldDiffs = (beforeDoc = {}, afterDoc = {}, platformsMap = {}) => (
  UPDATED_DOC_COMPARE_FIELDS.reduce((acc, { key, label, isPlatforms }) => {
    const beforeVal = beforeDoc[key]
    const afterVal = afterDoc[key]
    if (isEqual(beforeVal, afterVal)) return acc

    if (isPlatforms) {
      const beforeIds = beforeVal?.length ? beforeVal : null
      const afterIds = afterVal?.length ? afterVal : null
      acc.push({
        key,
        label,
        isPlatforms: true,
        beforeIds,
        afterIds,
        beforeLabels: beforeIds?.map(id => platformsMap[id] || id) || null,
        afterLabels: afterIds?.map(id => platformsMap[id] || id) || null,
      })
    } else {
      acc.push({
        key,
        label,
        isPlatforms: false,
        beforeText: beforeVal ?? '',
        afterText: afterVal ?? '',
      })
    }
    return acc
  }, [])
)

/**
 * 從已存的 generateDiff docDiffs + 當下 docData 重建子本設定變更（舊快照相容）
 */
export const buildUpdatedDocFieldDiffsFromDocDiffs = (docDiffs = [], afterDoc = {}, platformsMap = {}) => {
  const flatDiffs = (Array.isArray(docDiffs) ? docDiffs : []).flatMap(d => d?.diffs || [])
  if (!flatDiffs.length) return []

  return UPDATED_DOC_COMPARE_FIELDS.reduce((acc, { key, label, isPlatforms }) => {
    const diff = flatDiffs.find(d => d.key === key)
    if (!diff) return acc

    const beforeVal = Object.prototype.hasOwnProperty.call(diff, 'oldValue')
      ? diff.oldValue
      : undefined
    const afterVal = Object.prototype.hasOwnProperty.call(diff, 'newValue')
      ? diff.newValue
      : afterDoc?.[key]

    if (isEqual(beforeVal, afterVal)) return acc

    if (isPlatforms) {
      const beforeIds = beforeVal?.length ? beforeVal : null
      const afterIds = afterVal?.length ? afterVal : null
      acc.push({
        key,
        label,
        isPlatforms: true,
        beforeIds,
        afterIds,
        beforeLabels: beforeIds?.map(id => platformsMap[id] || id) || null,
        afterLabels: afterIds?.map(id => platformsMap[id] || id) || null,
      })
    } else {
      acc.push({
        key,
        label,
        isPlatforms: false,
        beforeText: beforeVal ?? '',
        afterText: afterVal ?? '',
      })
    }
    return acc
  }, [])
}

/**
 * 發佈寫入 Activity Log 前：為各子本補上 updatedDocFieldDiffs（草稿彈窗「子本設定變更」）
 */
export const enrichPublishedDocsWithUpdatedDocFieldDiffs = (
  publishedDocsDataByDocId = {},
  {
    updatedDocByDocId = {},
    originalDocsDataByDocId = {},
    docsDataByDocId = {},
    platformsMap = {},
  } = {},
) => {
  const next = { ...publishedDocsDataByDocId }
  Object.keys(next).forEach((docId) => {
    if (!docId || docId.includes('master') || !next[docId]) return
    if (next[docId].updatedDocFieldDiffs?.length) {
      // 舊列補 labels（若有 platformsMap）
      if (!isEmpty(platformsMap)) {
        next[docId] = {
          ...next[docId],
          updatedDocFieldDiffs: next[docId].updatedDocFieldDiffs.map((row) => {
            if (!row?.isPlatforms) return row
            if (row.beforeLabels || row.afterLabels) return row
            return {
              ...row,
              beforeLabels: row.beforeIds?.map(id => platformsMap[id] || id) || null,
              afterLabels: row.afterIds?.map(id => platformsMap[id] || id) || null,
            }
          }),
        }
      }
      return
    }

    const updatedDocEntry = updatedDocByDocId[docId]
    if (updatedDocEntry) {
      const beforeDoc = originalDocsDataByDocId[docId] || {}
      const afterDoc = { ...(docsDataByDocId[docId] || {}), ...updatedDocEntry, docId }
      const rows = buildUpdatedDocFieldDiffs(beforeDoc, afterDoc, platformsMap)
      if (rows.length) next[docId] = { ...next[docId], updatedDocFieldDiffs: rows }
      return
    }

    const fromDocDiffs = buildUpdatedDocFieldDiffsFromDocDiffs(
      next[docId].docDiffs,
      next[docId].docData || docsDataByDocId[docId] || {},
      platformsMap,
    )
    if (fromDocDiffs.length) {
      next[docId] = { ...next[docId], updatedDocFieldDiffs: fromDocDiffs }
    }
  })
  return next
}

/**
 * 與 DraftChange.getPublishedDocsDataByDocId 相同：單一 section 要展開比對的 doc 清單
 * @see DraftChange.vue ~790-798
 */
export const computeSectionPublishDocIds = ({
  wallet,
  sectionData = {},
  originSectionData = {},
  sectionDataIsEmpty = false,
}) => {
  const sectionDocs = sectionData.docs || []
  const originSectionDocs = originSectionData.docs || []
  const allDocIds = sortDocIds([`${wallet}#master`, ...new Set([...sectionDocs, ...originSectionDocs])])
  const addedDocIds = sectionDataIsEmpty
    ? []
    : sectionDocs.filter(docId => !originSectionDocs.includes(docId))
  const removedDocIds = sectionDataIsEmpty
    ? []
    : originSectionDocs.filter(docId => !sectionDocs.includes(docId))

  return {
    sectionDocs,
    originSectionDocs,
    allDocIds,
    addedDocIds,
    removedDocIds,
  }
}

export const hasPublishedDocSectionDiffs = (docEntry = {}) => (
  !!(docEntry.diffDataBySectionId && !isEmpty(docEntry.diffDataBySectionId))
)

const summarizeDocDiffEffect = (docEntry = {}) => {
  let hasAdded = false
  let hasRemoved = false
  let hasModified = false

  Object.values(docEntry.diffDataBySectionId || {}).forEach((sectionEntry) => {
    const sectionDiff = sectionEntry.sectionDiffData
    if (sectionDiff?.type === 'added' || sectionEntry.sectionData?.added) hasAdded = true
    if (sectionDiff?.type === 'removed' || sectionEntry.sectionData?.deleted) hasRemoved = true
    if (sectionDiff?.type === 'modified') hasModified = true
    const fieldsDiffData = sectionEntry.fieldsDiffData || []
    fieldsDiffData.forEach((fieldDiff) => {
      if (fieldDiff.type === 'added' || fieldDiff.after?.added) hasAdded = true
      else if (fieldDiff.type === 'removed' || fieldDiff.after?.deleted) hasRemoved = true
      else if (fieldDiff.type === 'modified') hasModified = true
      else hasModified = true
    })
  })

  if (hasRemoved && !hasAdded && !hasModified) {
    return { effect: 'removed', effectLabel: '此區塊／參數將不再出現於該子本' }
  }
  if (hasAdded && !hasRemoved && !hasModified) {
    return { effect: 'added', effectLabel: '此區塊／參數將新增至該子本' }
  }
  return { effect: 'updated', effectLabel: '此子本內容將更新（與發佈預覽一致）' }
}

/**
 * 從 DraftChange 產出的 publishedDocsDataByDocId 推算 Activity Log「影響子本」
 * （與草稿彈窗左側樹相同：扣掉母本後、仍有 diff 的子本）
 */
export const deriveAffectedDocsFromPublishedDocsData = (
  publishedDocsDataByDocId = {},
  docsDataByDocId = {},
) => {
  const childDocIds = sortDocIds(
    Object.keys(publishedDocsDataByDocId).filter(docId => (
      docId && !docId.includes('master') && hasPublishedDocSectionDiffs(publishedDocsDataByDocId[docId])
    )),
  )

  return childDocIds.map((docId) => {
    const docEntry = publishedDocsDataByDocId[docId] || {}
    const shortTitle = docsDataByDocId[docId]?.docShortTitle
      || docEntry.docShortTitle
      || docId
    const { effect, effectLabel } = summarizeDocDiffEffect(docEntry)
    return {
      docId,
      docShortTitle: shortTitle,
      effect,
      effectLabel,
    }
  })
}

/**
 * 從 publishedDocsDataByDocId 取母本某 section 的 entry
 */
export const getMasterSectionEntry = (
  publishedDocsDataByDocId = {},
  { wallet, masterDocId, sectionId } = {},
) => {
  const masterId = masterDocId || (wallet ? `${wallet}#master` : null)
  if (!masterId || !sectionId) return null
  return publishedDocsDataByDocId?.[masterId]?.diffDataBySectionId?.[sectionId] || null
}

/** 區塊標題（與 DraftChange getSectionTitle 顯示對齊的精簡版） */
export const getPublishedSectionHeader = (sectionEntry = {}, fallback = '') => {
  const section = sectionEntry.sectionData
    || sectionEntry.sectionDiffData?.after
    || sectionEntry.originSectionData
  if (!section) return fallback
  const title = `${section.enTitle || ''} ${section.cnTitle || ''}`.trim()
  const remark = section.remark ? ` ${section.remark}` : ''
  return `${title}${remark}`.trim() || fallback
}

const getSectionFromEntry = (sectionEntry = {}) => (
  sectionEntry.sectionData
  || sectionEntry.sectionDiffData?.after
  || sectionEntry.originSectionData
  || {}
)

/** Sort Order 對照列：與 DraftChange 右側相同的 6.19. 階層編號 */
export const formatSortOrderChangeLabel = (
  section,
  sortOrder,
  docId,
  resolveParentSection = null,
) => {
  if (!section || sortOrder === undefined || sortOrder === null) return ''
  if (isRootParentSectionId(section.parentSectionId)) {
    return `${sortOrder + 1}.`
  }
  const parentSection = typeof resolveParentSection === 'function'
    ? resolveParentSection(section.wallet, section.parentSectionId, docId)
    : null
  const parentNo = parentSection && isRootParentSectionId(parentSection.parentSectionId)
    ? getSectionTitleDisplayNoForDoc(parentSection, docId)
    : getSectionSortOrderForDoc(parentSection, docId) + 1
  return `${parentNo}.${sortOrder + 1}.`
}

const formatSectionTitleNo = (section = {}, sectionsByWallet = {}, { wallet, docId } = {}) => {
  const w = wallet || section.wallet
  const live = (w && section.sectionId && sectionsByWallet?.[w]?.[section.sectionId]) || section
  const parentId = live.parentSectionId ?? section.parentSectionId
  if (isRootParentSectionId(parentId)) {
    const displayNo = docId && String(docId).includes('master')
      ? (live.sortOrder ?? section.sortOrder ?? 0) + 1
      : getSectionTitleDisplayNoForDoc(live, docId)
    return displayNo != null ? `${displayNo}.` : ''
  }
  const parent = w ? sectionsByWallet?.[w]?.[parentId] : null
  if (!parent) return ''
  const parentNo = isRootParentSectionId(parent.parentSectionId)
    ? getSectionTitleDisplayNoForDoc(parent, docId)
    : getSectionSortOrderForDoc(parent, docId) + 1
  const childNo = getSectionSortOrderForDoc(live, docId) + 1
  if (parentNo == null || childNo == null) return ''
  return `${parentNo}.${childNo}.`
}

export const resolveChangeTitleParts = (title = {}) => {
  if (Array.isArray(title.parts) && title.parts.length) return title.parts
  const text = String(title.default || '')
  return text ? [{ type: 'text', text }] : []
}

/** Activity Log 列表：section 分組標題（優先用發佈當下 sectionDiff 標題，否則對齊樹狀編號） */
export const getPublishedSectionGroupHeading = (
  sectionEntry = {},
  sectionsByWallet = {},
  { wallet, docId } = {},
) => {
  const fromDiff = resolveChangeTitleParts(sectionEntry.sectionDiffData?.title)
    .map(part => part.text)
    .join('')
    .replace(CHANGE_TITLE_PREFIX, '')
    .trim()
  if (fromDiff) return fromDiff

  const name = getPublishedSectionHeader(sectionEntry, '')
  if (!name) return ''
  const prefix = formatSectionTitleNo(getSectionFromEntry(sectionEntry), sectionsByWallet, { wallet, docId })
  return prefix ? `${prefix} ${name}` : name
}

export const getMasterPublishedDocEntry = (publishedDocsDataByDocId = {}) => {
  const hit = Object.entries(publishedDocsDataByDocId).find(([docId]) => String(docId).includes('master'))
  return hit ? hit[1] : null
}

const RELEASE_VER_DISPLAY_KEYS = ['content', 'updateDate']

const buildReleaseVerValueDiffs = (type, fieldId, row = {}) => (
  RELEASE_VER_DISPLAY_KEYS.map(key => ({
    path: `/row/${fieldId}/${key}`,
    key,
    oldValue: type === 'removed' ? row[key] : undefined,
    newValue: type === 'added' ? row[key] : undefined,
  }))
)

/** 新增／刪除若只存了佔位 diffs，從 before／after 補 content、updateDate */
export const expandReleaseVerRowDiffs = (fieldDiff = {}) => {
  const { type } = fieldDiff
  if (type !== 'added' && type !== 'removed') return fieldDiff.diffs || []
  const existing = fieldDiff.diffs || []
  if (existing.some(d => RELEASE_VER_DISPLAY_KEYS.includes(d?.key))) return existing
  const source = type === 'removed' ? fieldDiff.before : fieldDiff.after
  const fieldId = source?.fieldId || String(fieldDiff.path || '').split('/')[2] || ''
  return buildReleaseVerValueDiffs(type, fieldId, source || {})
}

const fieldTitleFallbackParts = (fieldDiff = {}) => {
  const scope = fieldDiff.after?.scope || fieldDiff.before?.scope || ''
  const fieldName = fieldDiff.after?.fieldName || fieldDiff.before?.fieldName || ''
  return [
    ...(scope ? [{ type: 'b', text: scope }] : []),
    ...(scope && fieldName ? [{ type: 'text', text: ' · ' }] : []),
    ...(fieldName ? [{ type: 'code', text: fieldName }] : []),
  ]
}

/**
 * 將 DraftChange section entry 轉成 PublishDiffBlock 可吃的 blocks
 * （與 DraftChange 右側區塊變更同一資料來源）
 */
export const buildMasterDiffBlocksFromSectionEntry = (sectionEntry = {}, liveExamples = {}) => {
  const blocks = []
  const tableColumns = sectionEntry.sectionDiffData?.after?.tableColumns
    || sectionEntry.sectionData?.tableColumns
    || sectionEntry.sectionDiffData?.before?.tableColumns
    || sectionEntry.originSectionData?.tableColumns
    || {}
  const beforeTableColumns = sectionEntry.sectionDiffData?.before?.tableColumns
    || sectionEntry.originSectionData?.tableColumns
    || {}
  const sectionDiff = sectionEntry.sectionDiffData
  const sectionExamples = mergeExampleMaps(
    sectionDiff?.after?.examples,
    sectionEntry.sectionData?.examples,
    liveExamples,
    sectionDiff?.before?.examples,
    sectionEntry.originSectionData?.examples,
  )
  const sectionBeforeExamples = mergeExampleMaps(
    sectionDiff?.before?.examples,
    sectionEntry.originSectionData?.examples,
    liveExamples,
  )
  const isAddOrRemove = type => type === 'added' || type === 'removed'
  const sectionType = sectionDiff?.type || 'modified'
  if (sectionDiff && (isAddOrRemove(sectionType) || sectionDiff.diffs?.length)) {
    blocks.push({
      type: sectionType,
      titleParts: resolveChangeTitleParts(sectionDiff.title),
      diffs: isAddOrRemove(sectionType) ? [] : (sectionDiff.diffs || []),
      tableColumns,
      beforeTableColumns,
      examples: sectionExamples,
      beforeExamples: sectionBeforeExamples,
    })
  }
  const fieldsDiffData = sectionEntry.fieldsDiffData || []
  const sectionId = sectionEntry.originSectionData?.sectionId
    || sectionEntry.sectionData?.sectionId
  fieldsDiffData.forEach((fieldDiff) => {
    const fieldType = fieldDiff?.type || 'modified'
    const fieldIsAddOrRemove = isAddOrRemove(fieldType)
    if (!fieldIsAddOrRemove && !fieldDiff?.diffs?.length) return
    const diffs = sectionId === 'ReleaseVer'
      ? expandReleaseVerRowDiffs(fieldDiff)
      : (fieldIsAddOrRemove ? [] : (fieldDiff.diffs || []))
    const titleParts = resolveChangeTitleParts(fieldDiff.title)
    blocks.push({
      type: fieldType,
      titleParts: titleParts.length ? titleParts : fieldTitleFallbackParts(fieldDiff),
      diffs,
      tableColumns,
      beforeTableColumns,
      examples: sectionExamples,
      beforeExamples: sectionBeforeExamples,
    })
  })
  return blocks
}

export const buildMasterDiffBlocksFromPublishedDocs = (publishedDocsDataByDocId = {}) => {
  const master = getMasterPublishedDocEntry(publishedDocsDataByDocId)
  if (!master) return []
  return Object.values(master.diffDataBySectionId || {}).flatMap(sectionEntry => (
    buildMasterDiffBlocksFromSectionEntry(sectionEntry)
  ))
}

const PUBLISH_WALLET_ORDER = ['multi', 'single']

const comparePublishWallet = (a, b) => {
  const ia = PUBLISH_WALLET_ORDER.indexOf(a)
  const ib = PUBLISH_WALLET_ORDER.indexOf(b)
  if (ia === -1 && ib === -1) return String(a || '').localeCompare(String(b || ''))
  if (ia === -1) return 1
  if (ib === -1) return -1
  return ia - ib
}

const walletFromPublishedDoc = (docId, docData) => {
  if (docData?.wallet) return docData.wallet
  const id = String(docId || '')
  if (id.includes('master')) return id.split('#')[0] || ''
  const parts = id.split('#')
  return parts.length >= 2 ? parts[1] : parts[0] || ''
}

export const isMasterPublishedDocId = docId => String(docId || '').includes('master')

export const getPublishedDocWallet = (docId, entry) => (
  walletFromPublishedDoc(docId, entry?.docData)
)

export const collectPublishedMasterDocIds = (publishedDocsDataByDocId = {}) => (
  Object.keys(publishedDocsDataByDocId || {})
    .filter(isMasterPublishedDocId)
    .sort((a, b) => comparePublishWallet(
      getPublishedDocWallet(a, publishedDocsDataByDocId[a]),
      getPublishedDocWallet(b, publishedDocsDataByDocId[b]),
    ))
)

/** 從指定 publishedDocs 條目組出 Activity Log Detail 的區塊變更 */
export const buildPublishDocSectionGroups = (
  publishedDocsDataByDocId = {},
  sectionsByWallet = {},
  docId = null,
) => {
  const entry = docId
    ? publishedDocsDataByDocId?.[docId]
    : getMasterPublishedDocEntry(publishedDocsDataByDocId)
  if (!entry) return []
  const resolvedDocId = entry.docId || docId || ''
  const wallet = walletFromPublishedDoc(resolvedDocId, entry.docData)
  return Object.entries(entry.diffDataBySectionId || {}).map(([sectionId, sectionEntry]) => {
    const liveExamples = sectionsByWallet?.[wallet]?.[sectionId]?.examples
    const blocks = buildMasterDiffBlocksFromSectionEntry(sectionEntry, liveExamples)
    if (!blocks.length) return null
    return {
      sectionId,
      docId: resolvedDocId,
      sectionEntry,
      heading: getPublishedSectionGroupHeading(sectionEntry, sectionsByWallet, {
        wallet,
        docId: resolvedDocId,
      }),
      blocks,
    }
  }).filter(Boolean)
}

export const buildPublishMasterSectionGroups = (
  publishedDocsDataByDocId = {},
  sectionsByWallet = {},
) => {
  const masterIds = collectPublishedMasterDocIds(publishedDocsDataByDocId)
  if (masterIds.length <= 1) {
    return buildPublishDocSectionGroups(publishedDocsDataByDocId, sectionsByWallet)
  }
  return masterIds.flatMap(docId => (
    buildPublishDocSectionGroups(publishedDocsDataByDocId, sectionsByWallet, docId)
  ))
}

/**
 * 將 section／field 物件上的 customizedVersion[docId] 攤平進本體，並移除整包 map
 * （對齊 DraftChange.getPublishedDocsDataByDocId 寫入 sectionData／before／after 的形狀）
 */
export const flattenCustomizedVersionForDoc = (obj, docId) => {
  if (!obj || typeof obj !== 'object') return obj
  const { customizedVersion, ...rest } = obj
  if (!customizedVersion) return obj
  const cvForDoc = customizedVersion[docId]
  if (!cvForDoc) return rest
  const { examples: _examples, ...cvFields } = cvForDoc
  return {
    ...rest,
    ...cvFields,
    docId: rest.docId || docId,
  }
}

/**
 * 正規化 publishedDocsDataByDocId 供 Activity Log 儲存：
 * 去掉各 section／field 上重複的完整 customizedVersion map，只保留該 doc 已攤平欄位
 */
export const normalizePublishedDocsDataForActivityLog = (publishedDocsDataByDocId = {}) => {
  const result = {}

  Object.entries(publishedDocsDataByDocId).forEach(([docId, entry]) => {
    if (!entry) return
    const nextEntry = { ...entry }
    // 未更版時不存 releaseContent，避免 HTML 佔掉 DynamoDB 空間
    if (!nextEntry.shouldRelease) {
      delete nextEntry.releaseContent
      if (nextEntry.docData && Object.prototype.hasOwnProperty.call(nextEntry.docData, 'releaseContent')) {
        nextEntry.docData = { ...nextEntry.docData }
        delete nextEntry.docData.releaseContent
      }
    }
    const nextBySection = {}

    Object.entries(entry.diffDataBySectionId || {}).forEach(([sectionId, sectionEntry]) => {
      const nextSection = { ...sectionEntry }

      if (nextSection.sectionData) {
        nextSection.sectionData = flattenCustomizedVersionForDoc(nextSection.sectionData, docId)
      }
      if (nextSection.originSectionData) {
        nextSection.originSectionData = flattenCustomizedVersionForDoc(nextSection.originSectionData, docId)
      }
      if (nextSection.sectionDiffData) {
        nextSection.sectionDiffData = {
          ...nextSection.sectionDiffData,
          ...(nextSection.sectionDiffData.before
            ? { before: flattenCustomizedVersionForDoc(nextSection.sectionDiffData.before, docId) }
            : {}),
          ...(nextSection.sectionDiffData.after
            ? { after: flattenCustomizedVersionForDoc(nextSection.sectionDiffData.after, docId) }
            : {}),
        }
      }
      if (nextSection.fieldsDiffData?.length) {
        nextSection.fieldsDiffData = nextSection.fieldsDiffData.map(fieldDiff => ({
          ...fieldDiff,
          ...(fieldDiff.before
            ? { before: flattenCustomizedVersionForDoc(fieldDiff.before, docId) }
            : {}),
          ...(fieldDiff.after
            ? { after: flattenCustomizedVersionForDoc(fieldDiff.after, docId) }
            : {}),
        }))
      }

      nextBySection[sectionId] = nextSection
    })

    nextEntry.diffDataBySectionId = nextBySection
    result[docId] = nextEntry
  })

  return result
}

/**
 * 從 publishedDocs 各子本的 docData 組出 DraftChange 吃的 docsDataByDocId
 */
export const docsDataFromPublishedDocs = (publishedDocsDataByDocId = {}, fallback = {}) => {
  const result = { ...fallback }
  Object.entries(publishedDocsDataByDocId || {}).forEach(([docId, entry]) => {
    if (!entry?.docData) return
    result[docId] = entry.docData
  })
  return result
}

/**
 * 發佈當下建議寫入 Activity Log 的快照結構（與 DraftChange 畫面一致）
 *
 * 主資料：publishedDocsDataByDocId（= DraftChange.getPublishedDocsDataByDocId 的結果）
 * 子本 platform／標題讀 publishedDocsDataByDocId[docId].docData，不再另存 docsDataByDocId
 *
 * 時間語意：Activity Log 條目的 `at`／detail 顯示時間 = 按下「發佈」當下時間（publishedAt）
 */
export const buildPublishActivityDetail = ({
  wallet,
  publishedDocsDataByDocId,
  docsDataByDocId = null,
  masterDocId,
  masterDocIds,
  editorUsername,
  /** 按下發佈的時間（字串，如 2026/09/23 14:39）；勿傳草稿存檔時間 */
  publishedAt,
  causedBy,
}) => {
  const masterId = masterDocId || (wallet ? `${wallet}#master` : null)

  let publishedWithDocData = { ...(publishedDocsDataByDocId || {}) }
  Object.keys(publishedWithDocData).forEach((docId) => {
    if (docId.includes('master')) return
    const live = docsDataByDocId?.[docId]
    const entry = publishedWithDocData[docId]
    if (!entry || entry.docData || !live) return
    publishedWithDocData[docId] = {
      ...entry,
      docData: live,
      docShortTitle: entry.docShortTitle || live.docShortTitle,
      docTitle: entry.docTitle || live.docTitle,
    }
  })

  // 去掉重複的完整 customizedVersion map，只留該 doc 已攤平欄位
  publishedWithDocData = normalizePublishedDocsDataForActivityLog(publishedWithDocData)
  // 母本存完整 diff，子本只存與母本不同的差集
  publishedWithDocData = compactPublishedDocsForActivityLog(publishedWithDocData)

  return {
    kind: 'publish',
    wallet,
    masterDocId: masterId,
    ...(Array.isArray(masterDocIds) && masterDocIds.length ? { masterDocIds } : {}),
    editorUsername,
    /** 發佈時間（非草稿存檔時間） */
    publishedAt: publishedAt || null,
    ...(causedBy ? { causedBy } : {}),
    /** DraftChange 左側樹 + 右側 diff 的同一份結果 */
    publishedDocsDataByDocId: publishedWithDocData,
  }
}

export const resolvePublishTargetDocId = (publishedDocsDataByDocId = {}, wallet = 'multi') => {
  const publishedDocIds = Object.keys(publishedDocsDataByDocId || {})
  const hasMaster = publishedDocIds.some(isMasterPublishedDocId)
  const childDocIds = publishedDocIds.filter(id => id && !isMasterPublishedDocId(id))
  if (!hasMaster && childDocIds.length === 1) return childDocIds[0]
  return publishedDocIds.find(isMasterPublishedDocId) || `${wallet}#master`
}

/** 寫入 Activity Log 的 wallet／target：跨 wallet 母本發佈用 docIds，不塞單一 docId */
export const resolvePublishLogTarget = (publishedDocsDataByDocId = {}, fallbackWallet = 'multi') => {
  const masterDocIds = collectPublishedMasterDocIds(publishedDocsDataByDocId)
  if (masterDocIds.length > 1) {
    return {
      wallet: null,
      target: { docIds: masterDocIds },
      masterDocId: null,
      masterDocIds,
    }
  }

  const wallet = (
    (masterDocIds[0] && getPublishedDocWallet(masterDocIds[0], publishedDocsDataByDocId[masterDocIds[0]]))
    || Object.values(publishedDocsDataByDocId || {}).find(d => d?.docData?.wallet)?.docData?.wallet
    || fallbackWallet
  )
  return {
    wallet,
    target: { docId: resolvePublishTargetDocId(publishedDocsDataByDocId, wallet) },
    masterDocId: masterDocIds[0] || `${wallet}#master`,
    masterDocIds: masterDocIds.length ? masterDocIds : undefined,
  }
}

const RELEASE_VER_ROW_COMPARE_KEYS = ['version', 'content', 'updateDate']
const RELEASE_VER_CHANGE_TITLE = {
  added: { en: 'Add', cn: '新增' },
  modified: { en: 'Update', cn: '更新' },
  removed: { en: 'Delete', cn: '删除' },
}

const releaseVerMainColumnValue = (sectionData = {}, row = {}) => {
  const columns = Object.values(sectionData.tableColumns || {})
  const mainColumnId = columns.find(({ sortOrder }) => sortOrder === 0)?.columnId || 'version'
  return row?.[mainColumnId] || ''
}

const buildReleaseVerRowTitle = (type, sectionData, row) => {
  const { en, cn } = RELEASE_VER_CHANGE_TITLE[type] || RELEASE_VER_CHANGE_TITLE.modified
  const main = releaseVerMainColumnValue(sectionData, row)
  return {
    en: `[${en}] ${main}`,
    cn: `[${cn}] ${main}`,
    default: `[${cn}] ${main}`,
  }
}

const isPresentReleaseVerRow = row => !!(row && !row.deleted)

/** 子本 ReleaseVer row 新增／刪除／修改 → fieldsDiffData（與 DraftChange generateDiff 同形） */
export const buildReleaseVerRowDiffs = ({
  beforeRowsById = {},
  afterRowsById = {},
  sectionData = {},
} = {}) => {
  const fieldsDiffData = []
  const ids = new Set([
    ...Object.keys(beforeRowsById || {}),
    ...Object.keys(afterRowsById || {}),
  ])

  ids.forEach((fieldId) => {
    const before = beforeRowsById?.[fieldId]
    const after = afterRowsById?.[fieldId]
    const beforeOk = isPresentReleaseVerRow(before)
    const afterOk = isPresentReleaseVerRow(after)

    if (!beforeOk && !afterOk) return

    if (!beforeOk && afterOk) {
      fieldsDiffData.push({
        path: `/row/${fieldId}`,
        type: 'added',
        before: null,
        after,
        diffs: buildReleaseVerValueDiffs('added', fieldId, after),
        title: buildReleaseVerRowTitle('added', sectionData, after),
      })
      return
    }

    if (beforeOk && !afterOk) {
      fieldsDiffData.push({
        path: `/row/${fieldId}`,
        type: 'removed',
        before,
        after: null,
        diffs: buildReleaseVerValueDiffs('removed', fieldId, before),
        title: buildReleaseVerRowTitle('removed', sectionData, before),
      })
      return
    }

    const diffs = RELEASE_VER_ROW_COMPARE_KEYS.reduce((acc, key) => {
      if (isEqual(before[key], after[key])) return acc
      acc.push({
        path: `/row/${fieldId}/${key}`,
        key,
        oldValue: before[key],
        newValue: after[key],
      })
      return acc
    }, [])
    if (!diffs.length) return

    fieldsDiffData.push({
      path: `/row/${fieldId}`,
      type: 'modified',
      before,
      after,
      diffs,
      title: buildReleaseVerRowTitle('modified', sectionData, after),
    })
  })

  return fieldsDiffData
}

export const buildReleaseVerPublishSnapshotEntry = ({
  docId,
  docsDataByDocId = {},
  sectionData = {},
  fieldsDiffData = [],
} = {}) => {
  if (!docId || !fieldsDiffData.length) return null
  const doc = docsDataByDocId[docId] || {}
  const section = {
    sectionId: 'ReleaseVer',
    ...sectionData,
  }
  return {
    docId,
    docTitle: doc.docTitle,
    docShortTitle: doc.docShortTitle,
    docData: doc,
    diffDataBySectionId: {
      ReleaseVer: {
        originSectionData: section,
        sectionData: section,
        fieldsDiffData,
      },
    },
  }
}

export const mergeReleaseVerPublishSnapshotEntry = (publishedDocsDataByDocId, docId, entry) => {
  if (!publishedDocsDataByDocId || !docId || !entry) return publishedDocsDataByDocId
  const existing = publishedDocsDataByDocId[docId]
  if (!existing) {
    publishedDocsDataByDocId[docId] = entry
    return publishedDocsDataByDocId
  }
  publishedDocsDataByDocId[docId] = {
    ...existing,
    diffDataBySectionId: {
      ...(existing.diffDataBySectionId || {}),
      ReleaseVer: entry.diffDataBySectionId.ReleaseVer,
    },
  }
  return publishedDocsDataByDocId
}
